/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import {
  EMPTY_STRING,
  ListItemResource,
  ListResource,
  ListResourceService,
  StateResource,
  StateService,
} from '@common/tech-shared';
import { inject, Injectable } from '@angular/core';
import { Resource } from '@ngxp/rest';
import { first, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CommandListResourceServiceConfig, CommandResource, CreateCommand, SaveCommandProps } from './command.model';
import { CommandService } from './command.service';

@Injectable()
export class CommandListResourceService<
  B extends Resource,
  T extends ListResource,
  I extends ListItemResource,
> extends ListResourceService<B, T, I> {
  private readonly commandService: CommandService = inject(CommandService);

  constructor(
    protected config: CommandListResourceServiceConfig<B>,
    /**
     * @deprecated Der Parameter wird nicht mehr gebraucht.
     */
    private readonly oldCommandService?: CommandService,
    /**
     * @deprecated Der Parameter wird nicht mehr gebraucht.
     */
    protected oldStateService?: StateService,
  ) {
    super(config);
  }

  public createListItem(
    toCreatelistItem: unknown,
    saveCommandProps: SaveCommandProps = { snackBarMessage: EMPTY_STRING }, //NOSONAR
  ): Observable<StateResource<CommandResource>> {
    return this.selectListResource().pipe(
      first(),
      switchMap((resource: T) =>
        this.createCommand(resource, this.config.create.linkRel, this.createCreateCommand(toCreatelistItem), saveCommandProps),
      ),
    );
  }

  private createCommand(
    resource: Resource,
    linkRel: string,
    command: CreateCommand,
    saveCommandProps: SaveCommandProps,
  ): Observable<StateResource<CommandResource>> {
    return this.commandService.createCommand({
      resource,
      linkRel,
      command: command,
      snackBarMessage: saveCommandProps.snackBarMessage,
    });
  }

  private createCreateCommand(body: unknown): CreateCommand {
    return { order: this.config.create.order, body };
  }
}
